import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Page } from '@/components/layout/Page'
import { PageHeading } from '@/components/layout/PageHeading'
import { EmptyState, ErrorState, errorMessage } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { FilterChip, chipBase, type ChipOption } from '@/components/queue/FilterChip'
import { KpiStrip } from '@/components/queue/KpiStrip'
import { NewTicketDialog } from '@/components/queue/NewTicketDialog'
import { QueueRow, QueueSkeleton, QueueTableHeader } from '@/components/queue/QueueTable'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { useCustomers, useProducts } from '@/hooks/useDirectory'
import { useNow } from '@/hooks/useNow'
import { useTicketCounts } from '@/hooks/useTicketCounts'
import { PAGE_SIZE, useTicketList } from '@/hooks/useTickets'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import {
  FILTER_PRIORITIES,
  FILTER_TYPES,
  chipValueText,
  clearChipFilters,
  hasChipFilters,
  parseQueueFilters,
  setListFilter,
  setTenantFilter,
  toSearchParams,
  type ListFilterKey,
  type QueueFilters,
} from '@/lib/queueFilters'
import { priorityLabel, typeLabel } from '@/lib/ticketLabels'
import { cn } from '@/lib/utils'
import { useAuth } from '@/providers/AuthProvider'

/** Artboard 01: the agent's home screen (customers see their own organisation's tickets). */
export default function QueuePage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const isCustomer = user?.kind === 'CUSTOMER'
  const canCreate = usePermission(PERMISSIONS.ticketsCreate)
  const [searchParams, setSearchParams] = useSearchParams()
  const filters = useMemo(() => parseQueueFilters(searchParams), [searchParams])
  const filtersKey = toSearchParams(filters).toString()

  const [newOpen, setNewOpen] = useState(false)
  const [search, setSearch] = useState(filters.q)
  const debouncedSearch = useDebouncedValue(search, 300)
  // Cursor stack: [] = first page. Reset whenever the filters change.
  const [cursors, setCursors] = useState<string[]>([])
  const [cursorsFor, setCursorsFor] = useState(filtersKey)
  if (cursorsFor !== filtersKey) {
    setCursorsFor(filtersKey)
    setCursors([])
  }

  const setFilters = (next: QueueFilters) => setSearchParams(toSearchParams(next))

  // URL → input when navigation changes `q` (sidebar link, back button).
  useEffect(() => {
    setSearch((current) => (current.trim() === filters.q ? current : filters.q))
  }, [filters.q])
  // Input → URL, debounced; typing replaces the history entry instead of stacking one per word.
  const latestParams = useRef(searchParams)
  useEffect(() => {
    latestParams.current = searchParams
  }, [searchParams])
  useEffect(() => {
    const current = parseQueueFilters(latestParams.current)
    const q = debouncedSearch.trim()
    if (q !== current.q) setSearchParams(toSearchParams({ ...current, q }), { replace: true })
  }, [debouncedSearch, setSearchParams])

  const now = useNow(30_000)
  const counts = useTicketCounts()
  const list = useTicketList(filters, cursors[cursors.length - 1] ?? null)
  const products = useProducts()
  const customers = useCustomers(!isCustomer)
  const ownCustomer = useCustomers(isCustomer).data?.[0]

  const productOptions: ChipOption[] = (products.data ?? []).map((p) => ({ value: p.key, label: p.name }))
  const customerOptions: ChipOption[] = (customers.data ?? []).map((c) => ({ value: c.slug, label: c.name }))
  // Tenant chip: only while exactly one customer is selected; its options are that client's tenants.
  const singleCustomer = filters.customer.length === 1 ? customers.data?.find((c) => c.slug === filters.customer[0]) : undefined
  const tenantOptions: ChipOption[] = (singleCustomer?.tenants ?? []).map((x) => ({ value: x.id, label: x.name }))
  const labelFrom = (opts: ChipOption[]) => (v: string) => opts.find((o) => o.value === v)?.label ?? v

  const chip = (key: ListFilterKey, label: string, options: ChipOption[], empty: string) => (
    <FilterChip
      key={key}
      label={label}
      valueText={chipValueText(key, filters[key], empty, labelFrom(options))}
      options={options}
      selected={filters[key]}
      onChange={(values) => setFilters(setListFilter(filters, key, values))}
    />
  )

  const singleProduct = filters.product.length === 1 ? productOptions.find((p) => p.value === filters.product[0]) : undefined
  const title = isCustomer
    ? t('queue.customerTitle')
    : singleProduct && filters.queue === 'all'
      ? t('queue.productTitle', { product: singleProduct.label })
      : t(`queue.titles.${filters.queue}`)
  const eyebrow = isCustomer
    ? ownCustomer
      ? t('queue.customerEyebrow', { customer: ownCustomer.name })
      : t('queue.customerEyebrowFallback')
    : t('queue.eyebrow')

  const page = list.data
  const items = page?.items ?? []
  const filtered = hasChipFilters(filters)

  return (
    <Page>
      <PageHeading
        eyebrow={eyebrow}
        title={title}
        titleClassName="text-ink-strong"
        actions={
          <>
            <input
              type="search"
              aria-label={t('queue.searchLabel')}
              placeholder={t('queue.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-[280px] shrink-0 rounded-md border border-line-strong px-3.5 py-[9px] text-body text-ink placeholder:text-ink-placeholder focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            {canCreate ? (
              <button
                type="button"
                onClick={() => setNewOpen(true)}
                className="rounded-md bg-primary px-4 py-2.5 text-body font-semibold text-white hover:bg-primary/90"
              >
                {t('queue.newTicket')}
              </button>
            ) : null}
          </>
        }
      />

      {!isCustomer ? <KpiStrip counts={counts.data} /> : null}

      <div className="flex flex-col">
        <div className="flex items-center gap-2 pb-4">
          <button
            type="button"
            onClick={() => setFilters(clearChipFilters(filters))}
            aria-pressed={!filtered}
            className={cn(
              chipBase,
              !filtered ? 'bg-ink font-medium text-white' : 'border border-line-strong text-ink-body hover:border-ink-placeholder',
            )}
          >
            {t('queue.chips.all')}
          </button>
          {chip(
            'priority',
            t('queue.chips.priority'),
            FILTER_PRIORITIES.map((p) => ({ value: p, label: priorityLabel(p) })),
            t('common.any'),
          )}
          {chip('product', t('queue.chips.product'), productOptions, t('common.any'))}
          {chip(
            'type',
            t('queue.chips.type'),
            FILTER_TYPES.map((v) => ({ value: v, label: typeLabel(v) })),
            t('common.any'),
          )}
          {!isCustomer ? chip('customer', t('queue.chips.customer'), customerOptions, t('common.all')) : null}
          {!isCustomer && tenantOptions.length > 0 ? (
            <FilterChip
              label={t('queue.chips.tenant')}
              valueText={filters.tenant ? labelFrom(tenantOptions)(filters.tenant) : t('common.all')}
              options={tenantOptions}
              selected={filters.tenant ? [filters.tenant] : []}
              onChange={(values) => setFilters(setTenantFilter(filters, values))}
            />
          ) : null}
          <div className="grow" />
          <span className="text-meta text-ink-muted">{t('queue.sortedBySla')}</span>
        </div>

        <div role="table" aria-label={title} aria-busy={list.isFetching}>
          <QueueTableHeader showCustomer={!isCustomer} />
          {list.isPending ? (
            <QueueSkeleton />
          ) : list.isError ? (
            <div className="pt-4">
              <ErrorState message={t('queue.loadError', { message: errorMessage(list.error) })} onRetry={() => void list.refetch()} />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              message={filtered ? t('queue.empty') : t('queue.emptyQueue')}
              action={
                filtered ? (
                  <Button variant="outline" size="sm" onClick={() => setFilters(clearChipFilters(filters))}>
                    {t('queue.clearFilters')}
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <div role="rowgroup" className={cn(list.isPlaceholderData && 'opacity-60')}>
              {items.map((ticket) => (
                <QueueRow key={ticket.id} ticket={ticket} now={now} showCustomer={!isCustomer} />
              ))}
            </div>
          )}
        </div>

        {page && items.length > 0 ? (
          <div className="flex items-center justify-between px-3 pt-4">
            <span className="text-meta text-ink-muted">
              {t('queue.showing', { count: cursors.length * PAGE_SIZE + items.length, total: page.total })}
            </span>
            <div className="flex items-center gap-5">
              {cursors.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setCursors((c) => c.slice(0, -1))}
                  className="text-meta font-medium text-ink hover:underline"
                >
                  {t('queue.previousPage')}
                </button>
              ) : null}
              {page.nextCursor ? (
                <button
                  type="button"
                  onClick={() => setCursors((c) => [...c, page.nextCursor as string])}
                  className="text-meta font-medium text-ink hover:underline"
                >
                  {t('queue.nextPage')}
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      {canCreate ? <NewTicketDialog open={newOpen} onOpenChange={setNewOpen} /> : null}
    </Page>
  )
}
